import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  IconButton,
  Fade,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudIcon from '@mui/icons-material/Cloud';
import MoneyIcon from '@mui/icons-material/Money';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PaymentOption } from '../../settings/PaymentSettingsTab';
import salesService, { QuickSaleOnlinePaymentWithInvoiceRequest, QuickSaleOnlinePaymentItem, OnlinePaymentSelectedOption } from '../../../../../services/salesService';
import { CartItem } from '../../types/product.types';
// ✅ YENİ: İndirim kaydı yardımcıları (fatura kalemi + fiş başlığı için)
import { buildItemDiscountFields, buildHeaderDiscountFields } from '../../utils/quickSaleDiscount';

// Platform logoları - Clearbit Logo API kullanılıyor
const PLATFORM_LOGOS: { [key: string]: { logo: string; bgColor: string; fallbackText: string } } = {
  'YEMEK SEPETİ': {
    logo: 'https://logo.clearbit.com/yemeksepeti.com',
    bgColor: '#FA0050', // Yemek Sepeti kırmızısı
    fallbackText: 'YS'
  },
  'GETİR YEMEK': {
    logo: 'https://logo.clearbit.com/getir.com',
    bgColor: '#5D3EBC', // Getir moru
    fallbackText: 'G'
  },
  'TRENDYOL YEMEK': {
    logo: 'https://logo.clearbit.com/trendyol.com',
    bgColor: '#F27A1A', // Trendyol turuncusu
    fallbackText: 'TY'
  },
  'MİGROS YEMEK': {
    logo: 'https://logo.clearbit.com/migros.com.tr',
    bgColor: '#F68B1F', // Migros turuncusu
    fallbackText: 'M'
  },
};

// ✅ YENİ: Fiş yazdırma için invoice item tipi
interface PrintReceiptItem {
  itemName: string;
  quantity: number;
  unitPrice: number;
  grossTotal: number;
  netTotal: number;
  vatRate: number;
  vatAmount: number;
}

interface OnlinePaymentModalProps {
  open: boolean;
  onClose: () => void;
  onlinePaymentOptions: PaymentOption[];
  total: number;
  // ✅ YENİ: Ödeme işlemi için gerekli props
  companyId: string;
  companyName?: string;
  screenId?: number;
  accountId: number;
  accountName?: string;
  cart: CartItem[];
  selectedWarehouseIds: string[];
  isRestaurantMode: boolean;
  currentCashier?: { id: number; name: string } | null;
  // ✅ YENİ: Sepete uygulanan indirim (fiş başlığına kaydetmek için)
  appliedDiscountType?: 'percentage' | 'amount' | null;
  lastAppliedDiscountAmount?: number;
  onPaymentComplete: (refNo: string, successMessage?: string) => void;
  // ✅ YENİ: Fiş yazdırma callback'i
  onPrintReceipt?: (invoiceNumber: string, paymentType: string, items: PrintReceiptItem[]) => void;
  // 🎫 NUMARA GARANTİSİ: Ödeme denemesi (API çağrısı) başladıktan sonra başarısız olursa çağrılır.
  // QuickSaleScreen bu callback ile sipariş numarasını yeniler (numara yakılır, tekrar kullanılmaz).
  onPaymentFailed?: () => void;
}

const OnlinePaymentModal: React.FC<OnlinePaymentModalProps> = ({
  open,
  onClose,
  onlinePaymentOptions,
  total,
  // ✅ YENİ props
  companyId,
  companyName,
  screenId,
  accountId,
  accountName,
  cart,
  selectedWarehouseIds,
  isRestaurantMode,
  currentCashier,
  appliedDiscountType = null,
  lastAppliedDiscountAmount = 0,
  onPaymentComplete,
  onPrintReceipt, // ✅ YENİ: Fiş yazdırma callback'i
  onPaymentFailed,
}) => {
  // Seçilen online ödeme yöntemi (buton tıklandığında set edilecek)
  const [selectedOption, setSelectedOption] = useState<PaymentOption | null>(null);
  
  // ✅ YENİ: Loading ve hata state'leri
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Sadece aktif olan online ödeme yöntemlerini filtrele
  const activeOptions = onlinePaymentOptions.filter(opt => opt.enabled && opt.type === 'online');

  // Modal kapanınca seçimi sıfırla
  const handleClose = () => {
    if (isProcessing) return; // İşlem devam ediyorsa kapatma
    setSelectedOption(null);
    setError(null);
    setSuccessMessage(null);
    onClose();
  };

  // Geri butonuna tıklandığında
  const handleBack = () => {
    if (isProcessing) return;
    setSelectedOption(null);
    setError(null);
  };

  // ✅ Ödeme yöntemi seçildiğinde - GERÇEK ÖDEME İŞLEMİ
  const handlePaymentMethodSelect = async (method: 'cash' | 'pos' | 'online') => {
    if (!selectedOption || isProcessing) return;

    console.log('🌐 [ONLİNE ÖDEME] Platform:', selectedOption.label, '- Yöntem:', method);
    
    setIsProcessing(true);
    setError(null);

    // 🎫 NUMARA GARANTİSİ: API çağrısı yapıldıysa true olur; request hazırlanırken
    // oluşan hatalar (validasyon vb.) deneme sayılmaz, numara yakılmaz.
    let attemptStarted = false;

    try {
      // Kasa/Banka ID'lerini belirle
      let cashboxId: number | null = null;
      let cashboxName: string | null = null;
      let bankAccountId: number | null = null;
      let bankAccountName: string | null = null;

      if (method === 'cash') {
        cashboxId = selectedOption.onlineCashboxId ? parseInt(selectedOption.onlineCashboxId, 10) : null;
        cashboxName = selectedOption.onlineCashboxName || null;
      } else if (method === 'pos') {
        bankAccountId = selectedOption.onlineBankAccountId ? parseInt(selectedOption.onlineBankAccountId, 10) : null;
        bankAccountName = selectedOption.onlineBankAccountName || null;
      } else if (method === 'online') {
        cashboxId = selectedOption.onlineDirectCashboxId ? parseInt(selectedOption.onlineDirectCashboxId, 10) : null;
        cashboxName = selectedOption.onlineDirectCashboxName || null;
      }

      // Request oluştur
      const items: QuickSaleOnlinePaymentItem[] = cart.map(item => ({
        ProductId: item.id,
        ProductName: item.name,
        StockCode: null, // CartItem'da stockCode yok
        WarehouseId: selectedWarehouseIds[0] || '',
        WarehouseName: null,
        Quantity: item.quantity,
        Unit: item.unit || 'adet',
        UnitPrice: item.price,
        VatRate: item.vatRate || 0,
        SctRate: 0, // CartItem'da sctRate yok
        PriceType: item.vatStatus === 'included' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED',
        HasRecipe: item.hasRecipe || false,
        HasMenu: item.hasMenu || false,
        SelectedOptions: item.selectedOptions?.map((so): OnlinePaymentSelectedOption => ({
          ProductId: so.itemId,
          ProductName: so.itemName,
          Quantity: so.quantity,
          IsOptional: true, // Opsiyon seçilmişse opsiyonludur
          HasExtraOption: !!so.selectedExtraOption,
          ExtraOptionProductId: so.selectedExtraOption?.optionItemId || null,
          ExtraOptionProductName: so.selectedExtraOption?.optionItemName || null,
          PriceDifference: so.selectedExtraOption?.priceDifference || 0,
        })) || null,
        TotalPriceDifference: item.totalPriceDifference || 0,
        // ✅ YENİ: İndirim kaydı (fatura kalemi)
        ...buildItemDiscountFields(item),
      }));

      const request: QuickSaleOnlinePaymentWithInvoiceRequest = {
        CompanyName: companyName || null,
        ScreenId: screenId || null,
        AccountId: accountId,
        AccountName: accountName || null,
        OnlinePlatform: selectedOption.label,
        PaymentMethod: method,
        CashboxId: cashboxId,
        CashboxName: cashboxName,
        BankAccountId: bankAccountId,
        SubAccountId: selectedOption.onlineSubAccountId || null,
        BankAccountName: bankAccountName,
        TotalAmount: total,
        Currency: 'TRY',
        Description: `${selectedOption.label} - ${method.toUpperCase()} ödemesi`,
        CreatedBy: currentCashier?.name || null,
        BranchId: null,
        IsRestaurantMode: isRestaurantMode,
        CashierId: currentCashier?.id || null,
        CashierName: currentCashier?.name || null,
        // ✅ YENİ: Sepete uygulanan indirim kaydı (fiş başlığı)
        ...buildHeaderDiscountFields(cart, appliedDiscountType, lastAppliedDiscountAmount),
        Items: items,
      };

      console.log('🌐 [ONLİNE ÖDEME] Request:', request);

      attemptStarted = true;
      const response = await salesService.processOnlinePaymentWithInvoice(request);

      console.log('🌐 [ONLİNE ÖDEME] Response:', response);

      if (response.Success) {
        console.log('✅ [ONLİNE ÖDEME] Başarılı:', response.ReferenceNumber);
        
        // ✅ Hemen modalı kapat ve callback'i çağır (bildirim dışarıda gösterilecek)
        const refNo = response.ReferenceNumber || '';
        const platformLabel = selectedOption.label;
        const paymentTypeLabel = method === 'cash' ? `${platformLabel} (Nakit)` : 
                                  method === 'pos' ? `${platformLabel} (POS)` : 
                                  `${platformLabel} (Online)`;
        
        // ✅ YENİ: Fiş yazdırma - ödeme başarılı olduğunda
        if (onPrintReceipt && cart.length > 0) {
          try {
            console.log('🖨️ [ONLİNE ÖDEME] Fiş yazdırma başlıyor, cart:', cart.length, 'ürün');
            const printItems: PrintReceiptItem[] = cart.map(item => ({
              itemName: item.name,
              quantity: item.quantity,
              unitPrice: item.price,
              grossTotal: item.price * item.quantity,
              netTotal: item.price * item.quantity,
              vatRate: item.vatRate || 0,
              vatAmount: 0,
            }));
            console.log('🖨️ [ONLİNE ÖDEME] printItems hazırlandı:', printItems.length, 'ürün');
            // Callback'i çağır ve bekle (async olabilir)
            await Promise.resolve(onPrintReceipt(refNo, paymentTypeLabel, printItems));
            console.log('🖨️ [ONLİNE ÖDEME] Fiş yazdırıldı:', paymentTypeLabel);
          } catch (printError) {
            console.error('🖨️ [ONLİNE ÖDEME] Fiş yazdırma hatası:', printError);
          }
        } else {
          console.warn('🖨️ [ONLİNE ÖDEME] Fiş yazdırılamadı - onPrintReceipt:', !!onPrintReceipt, 'cart.length:', cart.length);
        }
        
        // State'leri sıfırla ve kapat
        setSelectedOption(null);
        setError(null);
        setIsProcessing(false);
        onClose();
        
        // Ödeme tamamlandı callback'i çağır (bildirim QuickSaleScreen'de gösterilecek)
        onPaymentComplete(refNo, `${platformLabel} ödemesi başarılı! Ref: ${refNo}`);
      } else {
        setError(response.Message || 'Ödeme işlemi başarısız');
        setIsProcessing(false);
        console.error('❌ [ONLİNE ÖDEME] Hata:', response.Message);
        // 🎫 NUMARA: Deneme başladı ve başarısız oldu — numara yenilensin
        if (onPaymentFailed) onPaymentFailed();
      }
    } catch (err: any) {
      console.error('❌ [ONLİNE ÖDEME] Exception:', err);
      setError(err.message || 'Beklenmeyen bir hata oluştu');
      setIsProcessing(false);
      // 🎫 NUMARA: Deneme başladıysa (API çağrısı yapıldıysa) numara yenilensin
      if (attemptStarted && onPaymentFailed) onPaymentFailed();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: '#f8f9fa',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: 'info.main',
        color: 'white',
        py: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedOption && (
            <IconButton size="small" onClick={handleBack} sx={{ color: 'white', mr: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <CloudIcon />
          <Typography variant="h6" fontWeight="bold">
            {selectedOption ? selectedOption.label : 'Online Ödemeler'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Hata Mesajı */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Başarı Mesajı */}
        {successMessage && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            icon={<CheckCircleIcon fontSize="inherit" />}
          >
            {successMessage}
          </Alert>
        )}

        {/* Toplam Tutar */}
        <Box sx={{ 
          mb: 3, 
          p: 2, 
          bgcolor: 'white', 
          borderRadius: 2, 
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <Typography variant="body2" color="text.secondary">
            Toplam Tutar
          </Typography>
          <Typography variant="h4" fontWeight="bold" color="info.main">
            {total.toFixed(2)} ₺
          </Typography>
        </Box>

        {/* Platform seçilmemişse - Platform listesi */}
        {!selectedOption ? (
          activeOptions.length === 0 ? (
            <Box sx={{ 
              p: 4, 
              textAlign: 'center', 
              bgcolor: 'white', 
              borderRadius: 2,
              border: '2px dashed #e0e0e0'
            }}>
              <CloudIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                Henüz online ödeme yöntemi tanımlanmamış.
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Ayarlar → Ödeme sekmesinden "Online Ödeme Ekle" butonunu kullanarak ekleyebilirsiniz.
              </Typography>
            </Box>
          ) : (
            <Fade in={!selectedOption}>
              <Grid container spacing={2}>
                {activeOptions.map((option) => {
                  const platformInfo = PLATFORM_LOGOS[option.label];
                  
                  return (
                    <Grid item xs={12} sm={6} key={option.id}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={() => setSelectedOption(option)}
                        sx={{
                          py: 2.5,
                          borderRadius: 2,
                          bgcolor: platformInfo?.bgColor || 'info.main',
                          '&:hover': {
                            bgcolor: platformInfo?.bgColor ? `${platformInfo.bgColor}dd` : 'info.dark',
                            filter: 'brightness(0.9)',
                          },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {platformInfo ? (
                          <Avatar
                            src={platformInfo.logo}
                            alt={option.label}
                            sx={{ 
                              width: 48, 
                              height: 48, 
                              bgcolor: 'white',
                              '& img': {
                                objectFit: 'contain',
                                p: 0.5,
                              }
                            }}
                          >
                            {/* Fallback: Logo yüklenemezse kısaltma göster */}
                            <Typography variant="h6" fontWeight="bold" color={platformInfo.bgColor}>
                              {platformInfo.fallbackText}
                            </Typography>
                          </Avatar>
                        ) : (
                          <CloudIcon sx={{ fontSize: 40 }} />
                        )}
                        <Typography variant="subtitle1" fontWeight="bold">
                          {option.label}
                        </Typography>
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            </Fade>
          )
        ) : (
          // Platform seçildiyse - 3 ödeme yöntemi göster
          <Fade in={!!selectedOption}>
            <Box>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                <strong>{selectedOption.label}</strong> için ödeme yöntemini seçin:
              </Typography>
              
              <Grid container spacing={2}>
                {/* Nakit Ödeme */}
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => handlePaymentMethodSelect('cash')}
                    disabled={!selectedOption.onlineCashboxId || isProcessing}
                    sx={{
                      py: 3,
                      borderRadius: 2,
                      bgcolor: 'success.main',
                      '&:hover': {
                        bgcolor: 'success.dark',
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'grey.300',
                        color: 'grey.500',
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    {isProcessing ? (
                      <CircularProgress size={32} color="inherit" sx={{ mb: 1 }} />
                    ) : (
                      <MoneyIcon sx={{ fontSize: 32, mb: 1 }} />
                    )}
                    <Typography variant="subtitle1" fontWeight="bold">
                      NAKİT
                    </Typography>
                  </Button>
                </Grid>

                {/* POS Ödeme */}
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => handlePaymentMethodSelect('pos')}
                    disabled={!selectedOption.onlineBankAccountId || isProcessing}
                    sx={{
                      py: 3,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'grey.300',
                        color: 'grey.500',
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    {isProcessing ? (
                      <CircularProgress size={32} color="inherit" sx={{ mb: 1 }} />
                    ) : (
                      <CreditCardIcon sx={{ fontSize: 32, mb: 1 }} />
                    )}
                    <Typography variant="subtitle1" fontWeight="bold">
                      POS
                    </Typography>
                  </Button>
                </Grid>

                {/* Online Ödeme (şimdilik nakit kasasına kayıt ediliyor) */}
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => handlePaymentMethodSelect('online')}
                    disabled={!selectedOption.onlineDirectCashboxId || isProcessing}
                    sx={{
                      py: 3,
                      borderRadius: 2,
                      bgcolor: 'secondary.main',
                      '&:hover': {
                        bgcolor: 'secondary.dark',
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'grey.300',
                        color: 'grey.500',
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    {isProcessing ? (
                      <CircularProgress size={32} color="inherit" sx={{ mb: 1 }} />
                    ) : (
                      <LanguageIcon sx={{ fontSize: 32, mb: 1 }} />
                    )}
                    <Typography variant="subtitle1" fontWeight="bold">
                      ONLİNE
                    </Typography>
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f0f0f0' }}>
        <Button 
          onClick={handleClose} 
          variant="outlined" 
          color="inherit"
          fullWidth
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnlinePaymentModal;
